import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Armchair, Printer, X } from "lucide-react";
import {
  useAdminAuth,
  AdminLogin,
  AdminLoading,
  ConfirmDelete,
} from "./AdminAuth";
import { DeleteBtn, printDay } from "./AdminListPage";
import { DatePicker } from "./DatePicker";
import { EMPTY_BLOCKED } from "./siteContent";
import { TimeSlotSelect } from "./TimeSlotSelect";
import AdminBar from "./AdminBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full rounded-sm border border-[#102C1F]/25 bg-[#F4F1EB] px-3 py-2 text-sm text-[#102C1F] outline-none transition-colors duration-300 placeholder:text-[#102C1F]/40 focus:border-[#102C1F]";

export default function AdminTablesPage() {
  const [user, setUser] = useAdminAuth();
  const [tables, setTables] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [toDelete, setToDelete] = useState(null);
  const [editing, setEditing] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const todayIso = () => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
  };
  const [availDate, setAvailDate] = useState(todayIso);
  const [dayAvail, setDayAvail] = useState(null);
  const [editDayAvail, setEditDayAvail] = useState(null);
  const [dayStatus, setDayStatus] = useState({});

  const load = useCallback(async () => {
    const [t, r, ds] = await Promise.all([
      axios.get(`${API}/tables`),
      axios.get(`${API}/reservations`, { withCredentials: true }),
      axios.get(`${API}/availability/days`),
    ]);
    setTables(t.data);
    setReservations(r.data);
    setDayStatus(ds.data);
  }, []);

  useEffect(() => {
    if (!user) return;
    load().catch(() => {});
  }, [user, load]);

  useEffect(() => {
    if (!user || !availDate) return;
    let alive = true;
    axios
      .get(`${API}/availability/day`, { params: { date: availDate } })
      .then((r) => alive && setDayAvail(r.data))
      .catch(() => alive && setDayAvail(null));
    return () => {
      alive = false;
    };
  }, [user, availDate]);

  const editDate = editing?.date;
  useEffect(() => {
    if (!editDate) {
      setEditDayAvail(null);
      return;
    }
    let alive = true;
    axios
      .get(`${API}/availability/day`, { params: { date: editDate } })
      .then((r) => alive && setEditDayAvail(r.data))
      .catch(() => alive && setEditDayAvail(null));
    return () => {
      alive = false;
    };
  }, [editDate]);

  const emptyEntry = () => ({
    key: Math.random().toString(36).slice(2),
    name: "",
    phone: "",
    date: "",
    time: "",
    guests: 2,
  });
  const [entries, setEntries] = useState([emptyEntry()]);

  const updateEntry = (i, key, val) =>
    setEntries((prev) =>
      prev.map((x, j) => (j === i ? { ...x, [key]: val } : x))
    );

  const addEntry = () => setEntries((prev) => [...prev, emptyEntry()]);

  const removeEntry = (i) =>
    setEntries((prev) => prev.filter((_, j) => j !== i));

  const saveEntry = async (e, i) => {
    if (!e.name?.trim() || !e.phone?.trim()) {
      toast.error("Nom et téléphone obligatoires.");
      return;
    }
    if (!e.date || !e.time) {
      toast.error("Date et heure obligatoires.");
      return;
    }
    if (!e.guests || Number(e.guests) < 1) {
      toast.error("Nombre de personnes invalide.");
      return;
    }
    try {
      await axios.post(`${API}/reservations`, {
        name: e.name.trim(),
        phone: e.phone.trim(),
        date: e.date,
        time: e.time,
        guests: Number(e.guests),
      });
      toast.success("Réservation enregistrée !");
      setEntries((prev) =>
        prev.map((x, j) => (j === i ? { ...emptyEntry(), key: x.key } : x))
      );
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'enregistrer.");
    }
  };

  const saveEdit = async () => {    if (!editing.name?.trim() || !editing.phone?.trim()) {
      toast.error("Nom et téléphone obligatoires.");
      return;
    }
    if (!editing.date || !editing.time) {
      toast.error("Date et heure obligatoires.");
      return;
    }
    if (!editing.guests || Number(editing.guests) < 1) {
      toast.error("Nombre de personnes invalide.");
      return;
    }
    setSavingEdit(true);
    try {
      await axios.put(
        `${API}/reservations/${editing.id}`,
        {
          name: editing.name.trim(),
          phone: editing.phone.trim(),
          email: editing.email || "",
          date: editing.date,
          time: editing.time,
          guests: Number(editing.guests),
          status: editing.status,
        },
        { withCredentials: true }
      );
      toast.success("Réservation mise à jour !");
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'enregistrer.");
    } finally {
      setSavingEdit(false);
    }
  };

  const confirmDelete = async () => {    if (!toDelete) return;
    try {
      await axios.delete(`${API}/${toDelete.tipo}/${toDelete.id}`, {
        withCredentials: true,
      });
      if (toDelete.tipo === "tables") {
        setTables((prev) => prev.filter((t) => t.id !== toDelete.id));
      } else {
        await load();
      }
      toast.success("Supprimé.");
    } catch {
      toast.error("Impossible de supprimer.");
    }
    setToDelete(null);
  };

  if (user === null) return <AdminLoading />;

  if (user === false) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F4F1EB] px-5">
        <AdminLogin onSuccess={setUser} title="Tables" />
      </div>
    );
  }

  const activeReservations = reservations
    .filter(
      (r) => !["annulee", "terminee", "cancelled", "done"].includes(r.status)
    )
    .sort((a, b) => `${a.date}T${a.time}`.localeCompare(`${b.date}T${b.time}`));
  const tableNumero = (id) => tables.find((t) => t.id === id)?.numero;
  const dayReservations = activeReservations.filter((r) => r.date === availDate);
  const editFullSlots = (editDayAvail?.services || [])
    .filter(
      (s) => s.full || Math.max(0, editDayAvail.capacity - s.used) === 0
    )
    .flatMap((s) => (s.slots || []).map((sl) => sl.time));

  return (
    <div
      data-testid="admin-tables-page"
      className="min-h-screen bg-[#F4F1EB] text-[#102C1F]"
    >
      <AdminBar />
      <header className="bg-[#102C1F] text-[#F4F1EB]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-10">
          <span className="flex items-center gap-3 font-display text-xl italic md:text-2xl">
            <Armchair size={20} /> Tables
          </span>
          <Link
            data-testid="tables-dashboard-link"
            to="/admin"
            className="rounded-full border border-[#F4F1EB]/30 px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-[#F4F1EB] hover:text-[#102C1F]"
          >
            Gestion
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-12 md:px-10">
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <span
            data-testid="tables-count"
            className="text-xs uppercase tracking-[0.2em] text-[#102C1F]/50"
          >
            Enregistrement rapide
          </span>
          <button
            data-testid="table-add-btn"
            onClick={addEntry}
            className="ml-auto rounded-full border border-dashed border-[#102C1F]/40 px-6 py-3 text-xs uppercase tracking-[0.25em] text-[#102C1F]/70 transition-colors duration-300 hover:border-[#102C1F] hover:text-[#102C1F]"
          >
            + Ajouter une réservation
          </button>
        </div>

        <div className="overflow-x-auto rounded-sm border border-[#102C1F]/15">
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr className="bg-[#102C1F] text-left text-xs uppercase tracking-[0.15em] text-[#F4F1EB]">
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Téléphone</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Heure</th>
                <th className="px-4 py-3">Pers.</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr
                  key={e.key}
                  data-testid={`table-row-${i}`}
                  className="border-t border-[#102C1F]/10 bg-[#EDE8DE]"
                >
                  <td className="px-4 py-3">
                    <input
                      data-testid={`table-client-name-${i}`}
                      placeholder="Nom du client"
                      value={e.name}
                      onChange={(ev) => updateEntry(i, "name", ev.target.value)}
                      className={`${field} min-w-36 py-1.5 text-xs`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      data-testid={`table-client-phone-${i}`}
                      placeholder="Téléphone"
                      type="tel"
                      value={e.phone}
                      onChange={(ev) => updateEntry(i, "phone", ev.target.value)}
                      className={`${field} min-w-32 py-1.5 text-xs`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      data-testid={`table-booking-date-${i}`}
                      type="date"
                      value={e.date}
                      onChange={(ev) => updateEntry(i, "date", ev.target.value)}
                      onClick={(ev) => {
                        try {
                          ev.currentTarget.showPicker?.();
                        } catch {
                          /* picker déjà ouvert */
                        }
                      }}
                      className={`${field} w-44 cursor-pointer px-2 py-1.5 text-xs`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <TimeSlotSelect
                      scope="reservations"
                      date={e.date}
                      value={e.time}
                      onChange={(v) => updateEntry(i, "time", v)}
                      testid={`table-booking-time-${i}`}
                      className={`${field} w-36 py-1.5 text-xs`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      data-testid={`table-booking-guests-${i}`}
                      type="number"
                      min="1"
                      max="64"
                      value={e.guests}
                      onChange={(ev) =>
                        updateEntry(i, "guests", ev.target.value)
                      }
                      className={`${field} w-20 py-1.5 text-xs`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        data-testid={`table-save-${i}`}
                        onClick={() => saveEntry(e, i)}
                        className="rounded-full bg-[#102C1F] px-5 py-2 text-xs uppercase tracking-[0.2em] text-[#F4F1EB] transition-colors duration-300 hover:bg-[#1B4030]"
                      >
                        Enregistrer
                      </button>
                      <DeleteBtn
                        testid={`table-delete-${i}`}
                        onClick={() => removeEntry(i)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
              {entries.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-6 text-center text-sm text-[#102C1F]/50"
                  >
                    Aucune ligne — cliquez sur « + Ajouter une réservation ».
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>

      <section
        data-testid="tables-availability-section"
        className="mx-auto max-w-7xl px-5 pb-12 md:px-10"
      >
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <p className="text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
            Disponibilité par jour
          </p>
          <div className="w-44">
            <DatePicker
              testid="availability-date-input"
              value={availDate}
              onChange={setAvailDate}
              blocked={EMPTY_BLOCKED}
              allowPast
              dayStatus={dayStatus}
            />
          </div>
          {dayAvail && !dayAvail.closed && dayAvail.services.length > 0 && (
            <span
              data-testid="availability-total"
              className="text-xs text-[#102C1F]/50"
            >
              Total du jour : {dayAvail.total_used}{" "}
              {dayAvail.total_used === 1 ? "personne" : "personnes"}
            </span>
          )}
        </div>
        {dayAvail &&
          (dayAvail.closed || dayAvail.services.length === 0 ? (
            <p
              data-testid="availability-closed"
              className="rounded-sm border border-red-700/30 bg-red-700/10 px-4 py-3 text-sm text-red-700"
            >
              Fermé ce jour.
            </p>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {dayAvail.services.map((s, si) => {
                const remaining = Math.max(0, dayAvail.capacity - s.used);
                return (
                <div
                  key={s.name}
                  data-testid={`availability-service-${si}`}
                  className="rounded-sm border border-[#102C1F]/15 bg-[#EDE8DE] px-4 py-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium">{s.name}</p>
                    {s.privatised || remaining === 0 ? (
                      <span
                        data-testid={`availability-full-${si}`}
                        className="rounded-full border border-red-700/50 bg-red-700/10 px-3 py-1 text-[11px] uppercase tracking-[0.15em] text-red-700"
                      >
                        {s.privatised ? "Privatisé" : "Complet"}
                      </span>
                    ) : (
                      <span
                        data-testid={`availability-open-${si}`}
                        className="rounded-full border border-[#102C1F] bg-[#102C1F] px-3 py-1 text-[11px] uppercase tracking-[0.15em] text-[#F4F1EB]"
                      >
                        Places disponibles
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-[#102C1F]/60">
                    {s.used}{" "}
                    {s.used === 1 ? "personne réservée" : "personnes réservées"}
                  </p>
                  <p
                    data-testid={`availability-remaining-${si}`}
                    className={`mt-1 text-sm font-medium ${remaining === 0 ? "text-red-700" : ""}`}
                  >
                    {remaining}{" "}
                    {remaining === 1 ? "place restante" : "places restantes"}
                  </p>
                </div>
                );
              })}
            </div>
          ))}
      </section>

      <section
        data-testid="tables-map-section"
        className="mx-auto max-w-7xl px-5 pb-12 md:px-10"
      >
        <p className="mb-3 text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
          Plan de salle — {availDate.split("-").reverse().join("/")}
        </p>
        {tables.filter((t) => t.id).length === 0 ? (
          <p className="text-sm text-[#102C1F]/60">Aucune table configurée.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {tables
              .filter((t) => t.id)
              .map((t) => {
                const resv = activeReservations.find(
                  (r) => r.table_id === t.id && r.date === availDate
                );
                const manual =
                  !resv && t.client_name?.trim() && t.booking_date === availDate;
                const occupied = !!(resv || manual);
                const state = !t.disponivel
                  ? "indisponible"
                  : occupied
                    ? "occupee"
                    : "libre";
                return (
                  <div
                    key={t.id}
                    data-testid={`map-table-${t.numero}`}
                    className={`rounded-sm border px-4 py-4 transition-colors duration-300 ${
                      state === "libre"
                        ? "border-[#102C1F] bg-[#102C1F] text-[#F4F1EB]"
                        : state === "occupee"
                          ? "border-red-700/50 bg-red-700/10 text-red-700"
                          : "border-[#102C1F]/15 bg-[#EDE8DE] text-[#102C1F]/40"
                    }`}
                  >
                    <p className="font-display text-2xl italic">
                      Table {t.numero}
                    </p>
                    <p className="text-xs opacity-70">{t.lugares} places</p>
                    <p className="mt-1.5 text-[11px] uppercase tracking-[0.15em]">
                      {state === "libre"
                        ? "Libre"
                        : state === "occupee"
                          ? "Occupée"
                          : "Indisponible"}
                    </p>
                    {resv && (
                      <p className="mt-0.5 text-xs">
                        {resv.name} · {resv.time.replace(":", "h")}
                      </p>
                    )}
                    {manual && (
                      <p className="mt-0.5 text-xs">
                        {t.client_name}
                        {t.booking_time
                          ? ` · ${t.booking_time.replace(":", "h")}`
                          : ""}
                      </p>
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </section>

      <section
        data-testid="tables-reservations-section"
        className="mx-auto max-w-7xl px-5 pb-12 md:px-10"
      >
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <p className="text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
            Réservations du {availDate.split("-").reverse().join("/")} (
            {dayReservations.length})
          </p>
          {dayReservations.length > 0 && (
            <button
              data-testid="print-day-reservations-btn"
              onClick={() => printDay("reservations", availDate, dayReservations)}
              className="flex items-center gap-1.5 rounded-full border border-[#102C1F]/25 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-[#102C1F]/70 transition-colors duration-300 hover:bg-[#102C1F] hover:text-[#F4F1EB]"
            >
              <Printer size={13} /> Imprimer
            </button>
          )}
        </div>
        <div className="space-y-3">
          {dayReservations.length === 0 && (
            <p className="text-sm text-[#102C1F]/60">
              Aucune réservation ce jour.
            </p>
          )}
          {dayReservations.map((r, i) => (
            <div
              key={r.id}
              data-testid={`tables-reservation-${i}`}
              className="flex flex-wrap items-baseline justify-between gap-2 rounded-sm border border-[#102C1F]/15 bg-[#EDE8DE] px-4 py-3"
            >
              <span className="text-sm font-medium">
                {r.privatisation
                  ? "Restaurant entier"
                  : r.table_id
                    ? `Table ${tableNumero(r.table_id) ?? "?"}`
                    : "Sans table"}{" "}
                · le {r.date.split("-").reverse().join("/")} à{" "}
                {r.time.replace(":", "h")} — {r.guests}{" "}
                {r.guests === 1 ? "pers." : "pers."}
              </span>
              <span className="flex flex-wrap items-center gap-3 text-sm text-[#102C1F]/70">
                {r.name} · {r.phone}
                {r.email ? ` · ${r.email}` : ""}
                <button
                  data-testid={`tables-edit-${i}`}
                  onClick={() => setEditing({ ...r })}
                  className="rounded-full border border-[#102C1F]/40 px-4 py-1.5 text-xs uppercase tracking-[0.15em] text-[#102C1F] transition-colors duration-300 hover:bg-[#102C1F] hover:text-[#F4F1EB]"
                >
                  Modifier
                </button>
                <DeleteBtn
                  testid={`tables-reservation-delete-${i}`}
                  onClick={() =>
                    setToDelete({
                      tipo: "reservations",
                      id: r.id,
                      name: `${r.name} — le ${r.date.split("-").reverse().join("/")} à ${r.time.replace(":", "h")}`,
                      table_id: r.table_id || "",
                    })
                  }
                />
              </span>
            </div>
          ))}
        </div>
      </section>

      {editing && (
        <div
          data-testid="edit-reservation-modal"
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 px-5"
        >
          <div className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-sm border border-[#102C1F]/20 bg-[#F4F1EB] p-8">
            <button
              data-testid="edit-reservation-close"
              onClick={() => setEditing(null)}
              aria-label="Fermer"
              className="absolute right-4 top-4 text-[#102C1F]/50 transition-colors duration-300 hover:text-[#102C1F]"
            >
              <X size={20} />
            </button>
            <h3 className="mb-6 font-display text-2xl italic tracking-tight">
              Modifier la réservation
            </h3>
            <div className="space-y-4">
              <label className="block">
                <span className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
                  Nom *
                </span>
                <input
                  data-testid="edit-reservation-name"
                  value={editing.name}
                  onChange={(e) =>
                    setEditing({ ...editing, name: e.target.value })
                  }
                  className={field}
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
                    Téléphone *
                  </span>
                  <input
                    data-testid="edit-reservation-phone"
                    value={editing.phone}
                    onChange={(e) =>
                      setEditing({ ...editing, phone: e.target.value })
                    }
                    className={field}
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
                    E-mail
                  </span>
                  <input
                    data-testid="edit-reservation-email"
                    type="email"
                    value={editing.email || ""}
                    onChange={(e) =>
                      setEditing({ ...editing, email: e.target.value })
                    }
                    className={field}
                  />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
                    Date *
                  </span>
                  <DatePicker
                    testid="edit-reservation-date"
                    value={editing.date}
                    onChange={(v) => setEditing({ ...editing, date: v })}
                    blocked={EMPTY_BLOCKED}
                    allowPast
                    dayStatus={dayStatus}
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
                    Heure *
                  </span>
                  <TimeSlotSelect
                    scope="reservations"
                    date={editing.date}
                    value={editing.time}
                    onChange={(v) => setEditing({ ...editing, time: v })}
                    testid="edit-reservation-time"
                    className={field}
                    fullSlots={editFullSlots}
                    forceEnabled
                  />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
                    Personnes *
                  </span>
                  <input
                    data-testid="edit-reservation-guests"
                    type="number"
                    min="1"
                    value={editing.guests}
                    onChange={(e) =>
                      setEditing({ ...editing, guests: e.target.value })
                    }
                    className={field}
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
                    Statut
                  </span>
                  <select
                    data-testid="edit-reservation-status"
                    value={editing.status}
                    onChange={(e) =>
                      setEditing({ ...editing, status: e.target.value })
                    }
                    className={field}
                  >
                    <option value="confirmee">Confirmée</option>
                    <option value="annulee">Annulée</option>
                    <option value="terminee">Terminée</option>
                  </select>
                </label>
              </div>
              <button
                data-testid="edit-reservation-save"
                onClick={saveEdit}
                disabled={savingEdit}
                className="w-full rounded-full bg-[#102C1F] px-5 py-3 text-xs uppercase tracking-[0.25em] text-[#F4F1EB] transition-colors duration-300 hover:bg-[#1B4030] disabled:opacity-50"
              >
                {savingEdit ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDelete
        item={toDelete}
        onCancel={() => setToDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
